import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Sparkles,
  Check,
  X,
  Trash2,
  Eye,
  Download,
  Filter,
  RefreshCw,
  Copy,
  Loader2,
  FileText,
  Send,
  Archive,
  ArchiveRestore,
  Pencil,
  MessageSquare,
} from 'lucide-react';
import OtterAvatar from '../components/OtterAvatar';
import {
  listFeedback,
  updateFeedback,
  deleteFeedback,
  regenerateFeedbackPrompt,
  exportFeedbackUrl,
  feedbackScreenshotUrl,
  feedbackVideoUrl,
} from '../services/api';
import { useAuth } from '../context/AuthContext';

const TYPES = {
  bug: { label: '🐛 Bug', color: 'red' },
  amelioration: { label: '✨ Amélioration', color: 'amber' },
  feature: { label: '💡 Feature', color: 'indigo' },
};

const FeedbackAdminPage = () => {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('actifs'); // 'actifs' | 'archives'
  const [onlyUnexported, setOnlyUnexported] = useState(false);
  const [selected, setSelected] = useState(null); // detail view
  const [regenerating, setRegenerating] = useState(false);
  const [previewExport, setPreviewExport] = useState(null); // {format, items, opts}
  const [previewLoading, setPreviewLoading] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await listFeedback(undefined, true);
      setItems(res.data || []);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh when a new feedback is submitted from the floating loutre button
  useEffect(() => {
    const onCreated = () => loadData();
    window.addEventListener('feedback:created', onCreated);
    return () => window.removeEventListener('feedback:created', onCreated);
  }, []);

  const handleDelete = async (item) => {
    if (!window.confirm(`Supprimer définitivement cette modification ?`)) return;
    try {
      await deleteFeedback(item.id);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
      if (selected && selected.id === item.id) setSelected(null);
      toast.success('Supprimé');
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleRegenerate = async (item) => {
    setRegenerating(true);
    try {
      const res = await regenerateFeedbackPrompt(item.id);
      const updated = { ...item, prompt_technique: res.data.prompt_technique };
      setItems((prev) => prev.map((x) => (x.id === item.id ? updated : x)));
      if (selected && selected.id === item.id) setSelected(updated);
      toast.success('Prompt régénéré ✨');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur IA');
    } finally {
      setRegenerating(false);
    }
  };

  // Édition après génération : description, précisions (commentaire) et prompt technique
  const [edit, setEdit] = useState(null); // { description, commentaire, prompt_technique, editingPrompt }
  const [savingEdit, setSavingEdit] = useState(false);
  useEffect(() => {
    if (selected) setEdit({ description: selected.description || '', commentaire: selected.commentaire || '', prompt_technique: selected.prompt_technique || '', editingPrompt: false, editingDesc: false });
    else setEdit(null);
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const handleSaveEdit = async (fields) => {
    if (!selected) return;
    setSavingEdit(true);
    try {
      const res = await updateFeedback(selected.id, fields);
      setItems((prev) => prev.map((x) => (x.id === selected.id ? res.data : x)));
      setSelected(res.data);
      setEdit((e) => ({ ...e, editingPrompt: false, editingDesc: false }));
      toast.success('Ticket mis à jour');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleToggleExported = async (item) => {
    try {
      const newVal = !item.exported;
      const res = await updateFeedback(item.id, { exported: newVal });
      setItems((prev) => prev.map((x) => (x.id === item.id ? res.data : x)));
      if (selected && selected.id === item.id) setSelected(res.data);
      toast.success(newVal ? 'Marqué comme exporté' : 'Statut export retiré');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  // « Fait » → marqué intégré + archivé en un clic
  const handleFait = async (item) => {
    try {
      const res = await updateFeedback(item.id, { statut: 'integre', archived: true });
      setItems((prev) => prev.map((x) => (x.id === item.id ? res.data : x)));
      if (selected && selected.id === item.id) setSelected(null);
      toast.success('✓ Fait → archivé');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  // « Pas fait » → suppression définitive
  const handlePasFait = async (item) => {
    if (!window.confirm('Pas fait : supprimer définitivement ce ticket ?')) return;
    try {
      await deleteFeedback(item.id);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
      if (selected && selected.id === item.id) setSelected(null);
      toast.success('Ticket supprimé');
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleUnarchive = async (item) => {
    try {
      const res = await updateFeedback(item.id, { archived: false, statut: 'a_traiter' });
      setItems((prev) => prev.map((x) => (x.id === item.id ? res.data : x)));
      if (selected && selected.id === item.id) setSelected(res.data);
      toast.success('Remis dans « À traiter »');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  // --- Preview & download flow ---
  const openExportPreview = async () => {
    if (!isAdmin) {
      toast.error('Réservé à l\'admin');
      return;
    }
    setPreviewLoading(true);
    try {
      const url = exportFeedbackUrl(15, 'all', 'json', onlyUnexported, { dryRun: true });
      const token = localStorage.getItem('token');
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('Preview failed');
      const data = await r.json();
      setPreviewExport({ items: data.items || [], mode: 'bulk', onlyUnexported });
    } catch (e) {
      toast.error('Erreur lors de la prévisualisation');
    } finally {
      setPreviewLoading(false);
    }
  };

  const openSingleExportPreview = async (item) => {
    if (!isAdmin) {
      toast.error('Réservé à l\'admin');
      return;
    }
    setPreviewLoading(true);
    try {
      const url = exportFeedbackUrl(1, 'all', 'json', false, { dryRun: true, ids: [item.id] });
      const token = localStorage.getItem('token');
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('Preview failed');
      const data = await r.json();
      setPreviewExport({ items: data.items || [], mode: 'single', singleId: item.id });
    } catch (e) {
      toast.error('Erreur lors de la prévisualisation');
    } finally {
      setPreviewLoading(false);
    }
  };

  const downloadFromPreview = async (fmt) => {
    if (!previewExport) return;
    const token = localStorage.getItem('token');
    let url;
    if (previewExport.mode === 'single') {
      url = exportFeedbackUrl(1, 'all', fmt, false, { ids: [previewExport.singleId] });
    } else {
      url = exportFeedbackUrl(15, 'all', fmt, previewExport.onlyUnexported);
    }
    try {
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('Export failed');
      const blob = fmt === 'csv' ? await r.blob() : new Blob([JSON.stringify(await r.json(), null, 2)], { type: 'application/json' });
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      a.download = previewExport.mode === 'single'
        ? `maag-modif-${previewExport.singleId.slice(0, 8)}-${new Date().toISOString().slice(0, 10)}.${fmt}`
        : `maag-modifications-${new Date().toISOString().slice(0, 10)}.${fmt}`;
      a.click();
      URL.revokeObjectURL(dlUrl);
      toast.success(`Export ${fmt.toUpperCase()} téléchargé`);
      setPreviewExport(null);
      loadData();
    } catch (e) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const copyPreviewToClipboard = () => {
    if (!previewExport) return;
    const payload = {
      exported_at: new Date().toISOString(),
      count: previewExport.items.length,
      items: previewExport.items,
    };
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    toast.success('Contenu copié dans le presse-papier ✓');
  };

  const copyPrompt = (text) => {
    navigator.clipboard.writeText(text || '');
    toast.success('Prompt copié dans le presse-papier ✓');
  };

  const actifs = items.filter((x) => !x.archived);
  const archives = items.filter((x) => !!x.archived);
  const filtered = (tab === 'archives' ? archives : actifs).filter((x) => !(onlyUnexported && x.exported));
  const unexportedValideCount = items.filter((x) => !x.archived && !x.exported).length;

  return (
    <div className="space-y-6" data-testid="feedback-admin-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
            <OtterAvatar size={56} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Modifications app</h1>
            <p className="text-sm text-slate-500">
              Centralise les remontées de l'équipe — la loutre formalise tout pour Emergent
            </p>
          </div>
        </div>
        {isAdmin && (
          <div className="flex flex-col items-end gap-1">
            <div className="flex gap-2">
              <button
                onClick={openExportPreview}
                disabled={previewLoading}
                data-testid="open-export-preview-btn"
                className="px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2 text-sm font-medium"
                title={onlyUnexported ? `Prévisualise les ${Math.min(15, unexportedValideCount)} derniers tickets à traiter non envoyés` : 'Prévisualise les 15 derniers tickets à traiter'}
              >
                {previewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                Aperçu & Export 15 dernières
              </button>
            </div>
            <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none" data-testid="only-unexported-toggle">
              <input
                type="checkbox"
                checked={onlyUnexported}
                onChange={(e) => setOnlyUnexported(e.target.checked)}
                className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
              />
              Exporter uniquement les non-envoyés
              {onlyUnexported && unexportedValideCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded font-medium">
                  {unexportedValideCount} à envoyer
                </span>
              )}
            </label>
          </div>
        )}
      </div>

      {/* Tabs : À traiter / Archivés */}
      <div className="flex items-center gap-2 border-b border-slate-200 pb-2 overflow-x-auto">
        <button
          onClick={() => setTab('actifs')}
          data-testid="tab-actifs"
          className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap ${
            tab === 'actifs' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          Tout <span className="ml-1 opacity-70">({actifs.length})</span>
        </button>
        <button
          onClick={() => setTab('archives')}
          data-testid="tab-archives"
          className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap flex items-center gap-1.5 ${
            tab === 'archives' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          <Archive className="w-3.5 h-3.5" /> Archivé <span className="ml-1 opacity-70">({archives.length})</span>
        </button>
        <span className="hidden sm:inline text-xs text-slate-400 ml-2">Pour chaque ticket : ✓ Fait → archivé · ✗ Pas fait → supprimé</span>
        <button
          onClick={loadData}
          className="ml-auto p-1.5 text-slate-500 hover:text-slate-700"
          title="Rafraîchir"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Items list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-slate-50 rounded-2xl border border-dashed border-slate-300">
          <OtterAvatar size={64} className="mx-auto" />
          <p className="mt-4 text-slate-500">{tab === 'archives' ? 'Aucun ticket archivé.' : 'Aucun ticket à traiter — tout est fait !'}</p>
          <p className="text-xs text-slate-400 mt-1">Cliquez sur la loutre 🦦 en bas à droite de n'importe quelle page pour proposer une idée.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map((item) => {
            const type = TYPES[item.type_demande] || { label: '—', color: 'slate' };
            return (
              <div
                key={item.id}
                className={`bg-white border rounded-xl p-4 hover:shadow-md transition ${
                  item.archived
                    ? 'border-slate-200 bg-slate-50 opacity-70'
                    : item.exported
                    ? 'border-emerald-200 bg-emerald-50/30'
                    : 'border-slate-200'
                }`}
                data-testid={`feedback-item-${item.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {item.archived && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800" data-testid={`fait-badge-${item.id}`}>
                          ✓ Fait
                        </span>
                      )}
                      <span className={`px-2 py-0.5 rounded-full text-xs bg-${type.color}-50 text-${type.color}-700 border border-${type.color}-200`}>
                        {type.label}
                      </span>
                      {item.exported && (
                        <span
                          className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300 flex items-center gap-1"
                          title={item.exported_at ? `Exporté le ${(item.exported_at || '').slice(0, 16).replace('T', ' ')}` : 'Exporté'}
                          data-testid={`exported-badge-${item.id}`}
                        >
                          <Send className="w-3 h-3" /> Envoyé à Emergent
                        </span>
                      )}
                      {item.page_label && (
                        <span className="text-xs text-slate-500">
                          · 📍 {item.page_label}
                        </span>
                      )}
                      <span className="text-xs text-slate-400 ml-auto">
                        {(item.created_at || '').slice(0, 16).replace('T', ' ')}
                      </span>
                    </div>
                    <p className="text-sm text-slate-800 line-clamp-2">{item.description}</p>
                    {item.commentaire && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded px-2 py-1 mt-1 line-clamp-2" data-testid={`commentaire-${item.id}`}>💬 {item.commentaire}</p>}
                    <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                      <span>👤 {item.submitted_by_name || item.submitted_by_email || 'Anonyme'}</span>
                      {item.screenshots && item.screenshots.length > 0 && (
                        <span>📷 {item.screenshots.length} capture{item.screenshots.length > 1 ? 's' : ''}</span>
                      )}
                      {item.videos && item.videos.length > 0 && (
                        <span>🎥 {item.videos.length} vidéo{item.videos.length > 1 ? 's' : ''}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => setSelected(item)}
                      data-testid={`view-${item.id}`}
                      className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg"
                      title="Voir le détail"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => openSingleExportPreview(item)}
                        data-testid={`export-single-${item.id}`}
                        className="p-2 text-indigo-500 hover:bg-indigo-50 rounded-lg"
                        title="Exporter uniquement cette modification (aperçu + téléchargement)"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    )}
                    {isAdmin && !item.archived && (
                      <>
                        <button
                          onClick={() => handleFait(item)}
                          data-testid={`fait-${item.id}`}
                          className="px-2.5 py-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg flex items-center gap-1"
                          title="Fait → archiver"
                        >
                          <Check className="w-3.5 h-3.5" /> Fait
                        </button>
                        <button
                          onClick={() => handlePasFait(item)}
                          data-testid={`pas-fait-${item.id}`}
                          className="px-2.5 py-1.5 text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg flex items-center gap-1"
                          title="Pas fait → supprimer"
                        >
                          <X className="w-3.5 h-3.5" /> Pas fait
                        </button>
                      </>
                    )}
                    {isAdmin && item.archived && (
                      <>
                        <button
                          onClick={() => handleUnarchive(item)}
                          data-testid={`unarchive-${item.id}`}
                          className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg"
                          title="Remettre dans « À traiter »"
                        >
                          <ArchiveRestore className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(item)}
                          data-testid={`delete-${item.id}`}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelected(null)}>
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-amber-50 to-orange-50">
              <div className="flex items-center gap-3">
                <OtterAvatar size={42} />
                <div>
                  <h3 className="font-bold text-amber-900">Détail de la modification</h3>
                  <p className="text-xs text-amber-700">
                    Soumis par {selected.submitted_by_name || selected.submitted_by_email} ·{' '}
                    {(selected.created_at || '').slice(0, 16).replace('T', ' ')}
                  </p>
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="p-1 hover:bg-amber-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${selected.archived ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                  {selected.archived ? '✓ Fait' : '⏳ À traiter'}
                </span>
                {selected.type_demande && (
                  <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-700">
                    {(TYPES[selected.type_demande] || {}).label}
                  </span>
                )}
                {selected.exported && (
                  <span
                    className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300 flex items-center gap-1"
                    data-testid="detail-exported-badge"
                  >
                    <Send className="w-3 h-3" /> Envoyé à Emergent
                    {selected.exported_at && (
                      <span className="opacity-70 font-normal">
                        · {(selected.exported_at || '').slice(0, 10)}
                      </span>
                    )}
                  </span>
                )}
                {selected.archived && (
                  <span
                    className="px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-200 text-slate-700 border border-slate-300 flex items-center gap-1"
                    data-testid="detail-archived-badge"
                  >
                    <Archive className="w-3 h-3" /> Archivé
                    {selected.archived_at && (
                      <span className="opacity-70 font-normal">
                        · {(selected.archived_at || '').slice(0, 10)}
                      </span>
                    )}
                  </span>
                )}
                {selected.page_label && (
                  <span className="text-xs text-slate-500">📍 {selected.page_label}</span>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <h4 className="text-sm font-semibold text-slate-700">Description initiale</h4>
                  {edit && !edit.editingDesc && (
                    <button onClick={() => setEdit({ ...edit, editingDesc: true })} className="px-2 py-1 text-xs bg-slate-100 text-slate-700 rounded hover:bg-slate-200 flex items-center gap-1" data-testid="detail-edit-desc-btn"><Pencil className="w-3 h-3" /> Modifier</button>
                  )}
                </div>
                {edit?.editingDesc ? (
                  <div className="space-y-2">
                    <textarea value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} rows={4} className="w-full text-sm rounded border border-slate-300 p-3 focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="detail-desc-input" />
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setEdit({ ...edit, editingDesc: false, description: selected.description || '' })} className="px-3 py-1.5 text-xs border border-slate-300 rounded hover:bg-slate-50">Annuler</button>
                      <button onClick={() => handleSaveEdit({ description: edit.description })} disabled={savingEdit || !edit.description.trim()} className="px-3 py-1.5 text-xs bg-slate-900 text-white rounded hover:bg-slate-700 disabled:opacity-50" data-testid="detail-save-desc-btn">Enregistrer</button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-800 bg-slate-50 rounded p-3 whitespace-pre-wrap">
                    {selected.description}
                  </p>
                )}
              </div>

              {/* Précisions / commentaire après génération du prompt */}
              <div data-testid="detail-commentaire-block">
                <h4 className="text-sm font-semibold text-slate-700 mb-1 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-amber-600" /> Mes précisions <span className="text-xs font-normal text-slate-400">(ce que vous voulez vraiment, ajustements, exemples…)</span></h4>
                <textarea
                  value={edit?.commentaire ?? ''}
                  onChange={(e) => setEdit({ ...edit, commentaire: e.target.value })}
                  rows={3}
                  placeholder="Ex. : je veux que ça s'applique aussi aux frais km / finalement pas besoin du bouton X…"
                  className="w-full text-sm rounded border border-amber-200 bg-amber-50/40 p-3 focus:outline-none focus:ring-2 focus:ring-amber-400"
                  data-testid="detail-commentaire-input"
                />
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[11px] text-slate-400">Inclus dans l'export et pris en compte si vous régénérez le prompt.</span>
                  <button
                    onClick={() => handleSaveEdit({ commentaire: edit?.commentaire ?? '' })}
                    disabled={savingEdit || (edit?.commentaire ?? '') === (selected.commentaire || '')}
                    className="px-3 py-1.5 text-xs bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-40 flex items-center gap-1"
                    data-testid="detail-save-commentaire-btn"
                  >
                    {savingEdit ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Enregistrer les précisions
                  </button>
                </div>
              </div>

              {selected.screenshots && selected.screenshots.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-1">
                    Captures ({selected.screenshots.length})
                  </h4>
                  <div className="grid grid-cols-3 gap-2">
                    {selected.screenshots.map((path, i) => (
                      <a
                        key={i}
                        href={feedbackScreenshotUrl(path)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block aspect-square rounded border border-slate-200 overflow-hidden hover:border-amber-400"
                      >
                        <img src={feedbackScreenshotUrl(path)} alt={`Capture ${i + 1}`} className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {selected.videos && selected.videos.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-1">
                    Vidéos ({selected.videos.length})
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {selected.videos.map((path, i) => (
                      <video
                        key={i}
                        src={feedbackVideoUrl(path)}
                        controls
                        preload="metadata"
                        className="w-full rounded border border-slate-200 bg-slate-900 max-h-72"
                      />
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-1">
                  <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-indigo-500" />
                    Prompt technique pour Emergent
                  </h4>
                  <div className="flex gap-1">
                    {selected.prompt_technique && !edit?.editingPrompt && (
                      <button
                        onClick={() => setEdit({ ...edit, editingPrompt: true, prompt_technique: selected.prompt_technique || '' })}
                        className="px-2 py-1 text-xs bg-slate-100 text-slate-700 rounded hover:bg-slate-200 flex items-center gap-1"
                        data-testid="detail-edit-prompt-btn"
                      >
                        <Pencil className="w-3 h-3" /> Modifier
                      </button>
                    )}
                    {selected.prompt_technique && (
                      <button
                        onClick={() => copyPrompt(selected.prompt_technique)}
                        className="px-2 py-1 text-xs bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" /> Copier
                      </button>
                    )}
                    {(isAdmin || selected.submitted_by_user_id) && (
                      <button
                        onClick={() => handleRegenerate(selected)}
                        disabled={regenerating}
                        className="px-2 py-1 text-xs bg-amber-100 text-amber-700 rounded hover:bg-amber-200 flex items-center gap-1 disabled:opacity-50"
                      >
                        {regenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                        Régénérer
                      </button>
                    )}
                  </div>
                </div>
                {edit?.editingPrompt ? (
                  <div className="space-y-2">
                    <textarea value={edit.prompt_technique} onChange={(e) => setEdit({ ...edit, prompt_technique: e.target.value })} rows={14} className="w-full bg-slate-900 text-slate-100 rounded-lg p-4 text-xs font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-indigo-400" data-testid="detail-prompt-input" />
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setEdit({ ...edit, editingPrompt: false })} className="px-3 py-1.5 text-xs border border-slate-300 rounded hover:bg-slate-50">Annuler</button>
                      <button onClick={() => handleSaveEdit({ prompt_technique: edit.prompt_technique })} disabled={savingEdit} className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50" data-testid="detail-save-prompt-btn">Enregistrer le prompt</button>
                    </div>
                  </div>
                ) : selected.prompt_technique ? (
                  <pre className="bg-slate-900 text-slate-100 rounded-lg p-4 text-xs whitespace-pre-wrap font-mono leading-relaxed max-h-96 overflow-y-auto">
                    {selected.prompt_technique}
                  </pre>
                ) : (
                  <p className="text-xs text-slate-500 italic bg-slate-50 p-3 rounded">
                    Aucun prompt généré. Cliquez sur "Régénérer" pour le créer.
                  </p>
                )}
              </div>
            </div>

            {isAdmin && (
              <div className="px-6 py-4 border-t border-slate-200 flex flex-wrap gap-2 justify-end">
                {!selected.archived ? (
                  <>
                    <button
                      onClick={() => handleFait(selected)}
                      data-testid="detail-fait-btn"
                      className="px-3 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-1"
                    >
                      <Check className="w-4 h-4" /> Fait → archiver
                    </button>
                    <button
                      onClick={() => handlePasFait(selected)}
                      data-testid="detail-pas-fait-btn"
                      className="px-3 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center gap-1"
                    >
                      <X className="w-4 h-4" /> Pas fait → supprimer
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => handleUnarchive(selected)}
                    data-testid="detail-unarchive-btn"
                    className="px-3 py-2 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 border border-slate-300 flex items-center gap-1"
                  >
                    <ArchiveRestore className="w-4 h-4" /> Remettre dans « À traiter »
                  </button>
                )}
                <button
                  onClick={() => handleToggleExported(selected)}
                  data-testid="toggle-exported-btn"
                  className={`px-3 py-2 text-sm rounded-lg flex items-center gap-1 ${
                    selected.exported
                      ? 'bg-amber-100 text-amber-800 hover:bg-amber-200 border border-amber-300'
                      : 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200 border border-emerald-300'
                  }`}
                  title={selected.exported ? 'Retirer le marqueur "Envoyé à Emergent"' : 'Marquer comme déjà envoyé à Emergent'}
                >
                  <Send className="w-4 h-4" />
                  {selected.exported ? 'Annuler "envoyé"' : 'Marquer comme envoyé'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Export Preview Modal */}
      {previewExport && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setPreviewExport(null)}
          data-testid="export-preview-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-indigo-50 to-purple-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <Eye className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-bold text-indigo-900">
                    Aperçu de l'export — {previewExport.mode === 'single' ? '1 modification' : `${previewExport.items.length} modification${previewExport.items.length > 1 ? 's' : ''}`}
                  </h3>
                  <p className="text-xs text-indigo-700">
                    Vérifie le contenu avant téléchargement. Le marqueur "Envoyé à Emergent" sera posé après téléchargement.
                  </p>
                </div>
              </div>
              <button onClick={() => setPreviewExport(null)} className="p-1 hover:bg-indigo-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-4 overflow-y-auto flex-1">
              {previewExport.items.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Filter className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Aucune modification à exporter avec ces critères.</p>
                  {previewExport.onlyUnexported && (
                    <p className="text-xs mt-2">Décoche "uniquement les non-envoyés" si tu veux ré-exporter celles déjà envoyées.</p>
                  )}
                </div>
              ) : (
                <>
                  {/* Compact summary list */}
                  <div className="mb-3 space-y-1.5">
                    {previewExport.items.map((it, idx) => (
                      <div
                        key={it.id}
                        className="flex items-center gap-2 text-xs px-2 py-1.5 bg-slate-50 rounded border border-slate-200"
                      >
                        <span className="text-slate-400 font-mono w-6 text-right">{idx + 1}.</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold bg-${(TYPES[it.type_demande] || { color: 'slate' }).color}-100 text-${(TYPES[it.type_demande] || { color: 'slate' }).color}-800`}>
                          {(TYPES[it.type_demande] || { label: '—' }).label}
                        </span>
                        <span className="text-slate-700 truncate flex-1">{it.description}</span>
                        {it.exported && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] bg-emerald-100 text-emerald-700 flex items-center gap-0.5 whitespace-nowrap">
                            <Send className="w-2.5 h-2.5" /> déjà envoyé
                          </span>
                        )}
                        <span className="text-slate-400 whitespace-nowrap">{(it.created_at || '').slice(0, 10)}</span>
                      </div>
                    ))}
                  </div>

                  {/* Raw JSON */}
                  <details className="border border-slate-200 rounded-lg overflow-hidden">
                    <summary className="px-3 py-2 bg-slate-100 cursor-pointer text-xs font-semibold text-slate-700 hover:bg-slate-200">
                      Voir le JSON brut ({previewExport.items.length} item{previewExport.items.length > 1 ? 's' : ''})
                    </summary>
                    <pre className="bg-slate-900 text-slate-100 text-xs p-4 max-h-72 overflow-auto font-mono">
                      {JSON.stringify({ count: previewExport.items.length, items: previewExport.items }, null, 2)}
                    </pre>
                  </details>
                </>
              )}
            </div>

            <div className="px-6 py-4 border-t border-slate-200 flex flex-wrap items-center gap-2 justify-end bg-slate-50">
              <button
                onClick={copyPreviewToClipboard}
                disabled={previewExport.items.length === 0}
                data-testid="preview-copy-btn"
                className="px-3 py-2 text-sm bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 disabled:opacity-50 flex items-center gap-1"
              >
                <Copy className="w-4 h-4" /> Copier JSON
              </button>
              <button
                onClick={() => downloadFromPreview('csv')}
                disabled={previewExport.items.length === 0}
                data-testid="preview-download-csv-btn"
                className="px-3 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
              >
                <FileText className="w-4 h-4" /> Télécharger CSV
              </button>
              <button
                onClick={() => downloadFromPreview('json')}
                disabled={previewExport.items.length === 0}
                data-testid="preview-download-json-btn"
                className="px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
              >
                <Download className="w-4 h-4" /> Télécharger JSON
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackAdminPage;
